// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Pure image maths.
 *
 * Everything here takes plain typed arrays and returns numbers, so all of it is unit-testable
 * without a canvas, a worker or a browser. The only browser-dependent step in the whole pipeline
 * is "decode a file into an RGBA grid at a fixed size", which lives in the worker.
 */

import type { DHash } from './types.ts';

/**
 * The working grid every measurement below is computed on.
 *
 * Square and fixed, so a portrait and a landscape frame of the same scene reduce to the same
 * grid — which is what makes dHash resolution- and aspect-independent, and what makes the
 * sharpness numbers within a group comparable at all.
 *
 * The size is 256 rather than 9x8 for a measured reason. Asking the browser to decode straight
 * down to 9x8 makes the hash depend on the browser's own resampling filter: with the same source
 * image and the same code, `resizeQuality: 'low'` and `'high'` produce hashes 21 bits apart on
 * hard-edged content — further apart than a 10%-per-edge crop, and two thirds of the way to an
 * unrelated image. Decoding once to a generous fixed grid and then doing the final reduction with
 * a first-party box filter makes the hash a pure function of pixels, so Chrome, Safari and Firefox
 * agree, and so it can be tested without a browser at all.
 */
export const WORK_SIZE = 256;

/**
 * Rec. 601 luma. Deliberately NOT Rec. 709.
 *
 * Every reference implementation of dHash and of variance-of-Laplacian in the wild goes through
 * PIL's `convert('L')` or OpenCV's `COLOR_BGR2GRAY`, both of which are 601. Matching them means a
 * hash computed here has the same meaning as one computed by the Python script the user was
 * otherwise going to run, which is worth more than a marginally better perceptual model.
 *
 * The `+ 0.5` rounds to nearest, which follows OpenCV. PIL's fixed-point path truncates instead,
 * so the two disagree by at most one level per channel — pure green is 150 here and 149 under
 * PIL. That is far below the threshold at which a dHash bit flips on real image data, and
 * rounding is the better of the two, so this does not chase PIL's off-by-one.
 */
export function toGray(rgba: Uint8ClampedArray | Uint8Array, out?: Uint8Array): Uint8Array {
  const n = rgba.length >> 2;
  const g = out ?? new Uint8Array(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) {
    g[i] = (0.299 * rgba[j]! + 0.587 * rgba[j + 1]! + 0.114 * rgba[j + 2]! + 0.5) | 0;
  }
  return g;
}

/**
 * Area-average downsample to an arbitrary target.
 *
 * Written by hand rather than delegated to `drawImage`, because canvas downscaling quality is
 * implementation-defined: Chrome, Safari and Firefox each pick a different filter, and a hash that
 * changes with the browser makes "this file is a duplicate of that one" browser-dependent. A box
 * filter is what PIL's `Image.ANTIALIAS`/`LANCZOS` approximates closely enough at these ratios,
 * and more importantly it is the same everywhere.
 */
export function boxDownsample(
  src: Uint8Array,
  sw: number,
  sh: number,
  dw: number,
  dh: number,
): Uint8Array {
  const dst = new Uint8Array(dw * dh);
  for (let y = 0; y < dh; y++) {
    const y0 = Math.floor((y * sh) / dh);
    const y1 = Math.max(y0 + 1, Math.floor(((y + 1) * sh) / dh));
    for (let x = 0; x < dw; x++) {
      const x0 = Math.floor((x * sw) / dw);
      const x1 = Math.max(x0 + 1, Math.floor(((x + 1) * sw) / dw));
      let sum = 0;
      let count = 0;
      for (let yy = y0; yy < y1; yy++) {
        const row = yy * sw;
        for (let xx = x0; xx < x1; xx++) {
          sum += src[row + xx]!;
          count++;
        }
      }
      dst[y * dw + x] = (sum / count + 0.5) | 0;
    }
  }
  return dst;
}

/**
 * dHash (difference hash), 64 bits.
 *
 * Downsample to 9x8 luminance, then set one bit per horizontally adjacent pair. Row-major, most
 * significant bit first.
 *
 * The polarity is `right > left`, matching Johannes Buchner's `imagehash` — `diff = pixels[:, 1:]
 * > pixels[:, :-1]` — which is itself following Krawetz's original. Inverting the polarity would
 * not change a single Hamming distance, so it makes no difference to how the tool behaves; it is
 * done this way so that the hex string in `dedup-report.csv` is the same string
 * `imagehash.dhash()` prints for the same photograph, and a user can check our arithmetic against
 * a library they already trust.
 *
 * WHAT THIS CATCHES, measured through a real browser decode on a 2000x1500 photo (distance from
 * the original): JPEG q40 re-encode 1 bit, PNG re-encode 1, WebP q50 1, downscale to 50% 1, to
 * 25% 1, to 10% 7, brightness x1.3 3 bits, a 6px blur 1.
 *
 * WHAT IT DOES NOT CATCH — stated in the interface, not buried here. A 5%-per-edge crop is
 * already 14 bits and a 10% crop is 26. A horizontal flip measures 30 bits and a 90-degree
 * rotation 31, against 31 for a completely unrelated photograph: mirrored and rotated copies are
 * not merely hard for this to find, they are indistinguishable from random.
 */
export function dhashFromGray(gray9x8: Uint8Array): DHash {
  if (gray9x8.length !== 72) throw new Error(`dhashFromGray expects a 9x8 grid (72 px), got ${gray9x8.length}`);
  let hi = 0;
  let lo = 0;
  let bit = 0;
  for (let y = 0; y < 8; y++) {
    const row = y * 9;
    for (let x = 0; x < 8; x++) {
      const set = gray9x8[row + x + 1]! > gray9x8[row + x]! ? 1 : 0;
      if (bit < 32) hi = ((hi << 1) | set) >>> 0;
      else lo = ((lo << 1) | set) >>> 0;
      bit++;
    }
  }
  return [hi, lo];
}

/** Convenience: full-resolution luminance straight to a hash. */
export function dhash(gray: Uint8Array, w: number, h: number): DHash {
  return dhashFromGray(boxDownsample(gray, w, h, 9, 8));
}

function popcount32(v: number): number {
  v = v - ((v >>> 1) & 0x55555555);
  v = (v & 0x33333333) + ((v >>> 2) & 0x33333333);
  v = (v + (v >>> 4)) & 0x0f0f0f0f;
  return (v * 0x01010101) >>> 24;
}

export function hamming(a: DHash, b: DHash): number {
  return popcount32((a[0] ^ b[0]) >>> 0) + popcount32((a[1] ^ b[1]) >>> 0);
}

export function hashToHex(h: DHash): string {
  return h[0].toString(16).padStart(8, '0') + h[1].toString(16).padStart(8, '0');
}

export function hexToHash(hex: string): DHash {
  if (!/^[0-9a-f]{16}$/i.test(hex)) throw new Error(`not a 64-bit hex hash: ${hex}`);
  return [parseInt(hex.slice(0, 8), 16) >>> 0, parseInt(hex.slice(8), 16) >>> 0];
}

/**
 * Variance of the Laplacian — the standard blur measure (Pech-Pacheco et al. 2000, popularised by
 * Rosebrock's "Blur detection with OpenCV").
 *
 * THE CAVEAT IS THE WHOLE STORY, and it is why this number is never shown as a verdict. The value
 * scales with resolution, with contrast, and with how much fine detail the scene happens to
 * contain: a sharp photograph of a clear sky scores lower than a badly out-of-focus photograph of
 * a brick wall. Any fixed "below N is blurry" threshold is therefore wrong on somebody's dataset.
 *
 * Loraprep only ever compares this WITHIN a group of near-identical frames, where resolution and
 * content are held constant and the comparison is the one thing it can honestly support: of these
 * four frames of the same shot, which is the sharpest?
 *
 * The 4-neighbour kernel [[0,1,0],[1,-4,1],[0,1,0]] is OpenCV's `CV_64F` Laplacian default.
 */
export function varianceOfLaplacian(gray: Uint8Array, w: number, h: number): { variance: number; meanAbs: number } {
  if (w < 3 || h < 3) return { variance: 0, meanAbs: 0 };
  let sum = 0;
  let sumSq = 0;
  let sumAbs = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    const row = y * w;
    for (let x = 1; x < w - 1; x++) {
      const i = row + x;
      const v = gray[i - w]! + gray[i + w]! + gray[i - 1]! + gray[i + 1]! - 4 * gray[i]!;
      sum += v;
      sumSq += v * v;
      sumAbs += v < 0 ? -v : v;
      n++;
    }
  }
  const mean = sum / n;
  return { variance: sumSq / n - mean * mean, meanAbs: sumAbs / n };
}

/**
 * How much of the image is a handful of exact colours.
 *
 * A photograph, even a heavily compressed one, has a long tail of luminance values because sensor
 * noise and demosaicing guarantee it. A screenshot, a diagram or an exported chart has large areas
 * of literally identical pixels. This measures the share of pixels sitting in the six most common
 * luminance buckets, which separates the two cleanly and costs one histogram pass.
 *
 * It is a heuristic and is presented as one — it ships switched OFF, because a legitimate training
 * image can be a flat studio shot on seamless white, and silently discarding those would be worse
 * than leaving three screenshots in.
 */
export function flatness(gray: Uint8Array): number {
  const hist = new Uint32Array(256);
  for (let i = 0; i < gray.length; i++) hist[gray[i]!]!++;
  const sorted = Array.from(hist).sort((a, b) => b - a);
  let top = 0;
  for (let i = 0; i < 6; i++) top += sorted[i]!;
  return top / gray.length;
}
